// @flow
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ICONS } from 'Components/utils/icon/icons';
import { FilterTransport } from 'Components/widgets/criteria/criteria-transport/filter-transport/FilterTransport.jsx';
import { CriteriaTrigger } from 'Components/widgets/criteria/CriteriaTrigger.jsx';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import {
  useCriteriaDisplayContext,
  ACTIONS,
} from 'Components/widgets/criteria/CriteriaDisplayContext';
import { t } from 'Src/i18n';
import { changeDepartureCity } from 'Store/faceting/facetingSearch.action';
import { filtersSelector } from 'Store/faceting/faceting.selector';
import { getDepartureCity } from 'Store/trip-criteria/tripCriteria.selector';

import { useTracking } from 'Tracking/tracking-api/useTracking';
import { COLORS } from 'Components/utils/color/colors';
import { OPEN_CRITERIA_TRANSPORT_EVENT } from 'Components/widgets/criteria/criteria-transport/constants';
import { DEPARTURE_CITY_FACET_ID } from 'Utils/faceting/constants';

type CriteriaTransportProps = {
  departureCitiesOptions: DepartureCities,
  isJoyview?: boolean,
  isLoading?: boolean,
};

export const CriteriaTransport = ({
  departureCitiesOptions,
  isJoyview = false,
  isLoading = false,
}: CriteriaTransportProps) => {
  const [criteriaContext, setCriteriaContext] = useContext(CriteriaContext);
  const [criteriaDisplayContext, dispatchCriteriaDisplayContext] = useCriteriaDisplayContext();
  const dispatch = useDispatch();
  const departureCity = useSelector(getDepartureCity);
  const facetingFilters = useSelector(filtersSelector);
  const track = useTracking({ actionGA: criteriaContext.trackingActionName });
  const [inputDepartureCity, setInputDepartureCity] = useState(departureCity);
  const noDepartureCitiesOptions = [
    {
      isDisabled: true,
      isSelected: true,
      label: t('booking.priceCalendar.priceStatus.noFlight'),
      value: '',
    },
  ];

  // Store update effect
  useEffect(
    () => {
      // 1. Sync local state with store updates
      if (inputDepartureCity !== departureCity) {
        setInputDepartureCity(departureCity);
      }
      // 2. Sync criteria validation with store updates
      setCriteriaContext({ criteriaTransportIsSet: Boolean(departureCity) });
    },
    [departureCity],
  );

  const openCriteriaEventHandler = () => {
    dispatchCriteriaDisplayContext({
      type: ACTIONS.OPEN_CRITERIA,
      payload: 'criteriaTransport',
    });
  };

  // Open/Close effect
  useEffect(() => {
    document.addEventListener(OPEN_CRITERIA_TRANSPORT_EVENT, openCriteriaEventHandler);
    return () =>
      document.removeEventListener(OPEN_CRITERIA_TRANSPORT_EVENT, openCriteriaEventHandler);
  }, []);

  const saveTransport = () => {
    if (inputDepartureCity !== departureCity) {
      dispatch(changeDepartureCity(inputDepartureCity));
    }

    dispatchCriteriaDisplayContext({
      type: ACTIONS.CLOSE_CRITERIA,
      payload: 'criteriaTransport',
    });
  };

  const getIcon = () => {
    if (departureCity === 'NO') {
      return ICONS.NO_TRANSPORT;
    }
    return ICONS.TRANSPORT;
  };

  const getLabel = () => {
    if (!criteriaContext.criteriaTransportIsSet) {
      return t('faceting.transport.departingFrom');
    }

    const selectedOption = departureCitiesOptions.find(item => item.value === departureCity);

    if (!selectedOption) {
      const departureCityFilter = facetingFilters.find(({ id }) => id === DEPARTURE_CITY_FACET_ID);
      return departureCityFilter ? departureCityFilter.label : '';
    }

    if (departureCity === 'NO') {
      return selectedOption.label;
    }

    return t('faceting.transport.titleFrom', {
      location: selectedOption.label,
    });
  };

  const buttonIsLoading =
    !departureCitiesOptions.length && isLoading && criteriaContext.criteriaTransportIsSet;

  const buttonLabel = (
    <span
      className="hidden lg:flex flex-row py-0 flex-1 overflow-hidden items-center whitespace-no-wrap button-default"
      style={{ height: '1px' }}
    >
      {getLabel()}
    </span>
  );

  const trackDepartureCityChange = (newDepartureCity: string) => {
    if (newDepartureCity) {
      const labelGA = `departureCity_${newDepartureCity}`;

      if (criteriaContext.trackingActionName) {
        track({
          labelGA,
        });
      }
    }
  };

  return (
    <CriteriaTrigger
      buttonLabel={buttonLabel}
      buttonIcon={getIcon()}
      buttonIsFilled={criteriaContext.criteriaTransportIsSet}
      trackingLabel="departureCity"
      isOpen={criteriaDisplayContext.criteriaTransportIsOpen}
      isJoyview={isJoyview}
      isLoading={buttonIsLoading}
      onClear={() => {
        dispatch(changeDepartureCity(''));
      }}
      onOpen={() => {
        dispatchCriteriaDisplayContext({
          type: ACTIONS.OPEN_CRITERIA,
          payload: 'criteriaTransport',
        });
      }}
      onClose={saveTransport}
      buttons={[
        {
          children: t('faceting.reset'),
          theme: 'link',
          onClick: () => {
            setInputDepartureCity('');
          },
          className: 'text-sm cursor-pointer',
        },
        {
          children: t('faceting.validate'),
          color: COLORS.WHITE,
          colorBg: COLORS.TURQUOISE,
          onClick: saveTransport,
        },
      ]}
    >
      <FilterTransport
        label={t('faceting.transport.subTitle')}
        optionsDepartureCities={
          !isLoading && !departureCitiesOptions.length
            ? noDepartureCitiesOptions
            : departureCitiesOptions
        }
        isDisabled={!isLoading && !departureCitiesOptions.length}
        isLoading={isLoading}
        informationLabel={t('faceting.transport.comment')}
        changeDepartureCity={(newDepartureCity: string) => {
          setInputDepartureCity(newDepartureCity);
          trackDepartureCityChange(newDepartureCity);
        }}
        departureCity={!departureCitiesOptions.length ? '' : inputDepartureCity}
        labelDepartingFrom={t('faceting.transport.departingFrom')}
        selectPlaceholder={t('faceting.transport.selectPlaceholder')}
        placeholderFrom={t('faceting.transport.titleFrom')}
      />
    </CriteriaTrigger>
  );
};
